import React from 'react';

/**
 * Card Component - Consistent card layout for content containers
 * 
 * Variants:
 * - default: Standard card with shadow
 * - outlined: Card with border, no shadow
 * - elevated: Card with stronger shadow
 * - flat: Card with no shadow or border
 */

interface CardProps {
  variant?: 'default' | 'outlined' | 'elevated' | 'flat';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: () => void;
  hoverable?: boolean;
}

const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  children,
  className = '',
  header,
  footer,
  onClick,
  hoverable = false
}) => {
  const baseClasses = [
    'bg-white rounded-lg transition-all duration-200',
    onClick || hoverable ? 'cursor-pointer' : ''
  ];

  const variantClasses = {
    default: [
      'shadow-sm border border-gray-200',
      hoverable || onClick ? 'hover:shadow-md' : ''
    ],
    outlined: [
      'border border-gray-300',
      hoverable || onClick ? 'hover:border-gray-400 hover:shadow-sm' : ''
    ],
    elevated: [
      'shadow-lg border border-gray-100',
      hoverable || onClick ? 'hover:shadow-xl' : ''
    ],
    flat: [
      'border-0 shadow-none',
      hoverable || onClick ? 'hover:bg-gray-50' : ''
    ]
  };

  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };

  const combinedClasses = [
    ...baseClasses,
    ...variantClasses[variant],
    className
  ].join(' ');

  const contentPadding = paddingClasses[padding];

  return (
    <div className={combinedClasses} onClick={onClick}>
      {header && (
        <div className={`${contentPadding} ${footer || children ? 'border-b border-gray-200' : ''}`}>
          {header}
        </div>
      )}
      
      {children && (
        <div className={`${contentPadding} ${header && footer ? 'border-b border-gray-200' : ''}`}>
          {children}
        </div>
      )}
      
      {footer && (
        <div className={contentPadding}>
          {footer}
        </div>
      )}
    </div>
  );
};

export default Card;
